// Performance Manager for optimal rendering
class PerformanceManager {
  constructor() {
    this.quality = "auto";
    this.fps = 60;
    this.frameTime = 0;
    this.lastTime = 0;
    this.frameCount = 0;
    this.qualitySettings = this.detectOptimalQuality();
    this.isLowPerformanceDevice = this.detectLowPerformanceDevice();

    this.initPerformanceMonitoring();
  }

  detectOptimalQuality() {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");

    if (!gl) {
      return this.getQualitySettings("low");
    }

    // Check GPU capabilities
    const renderer = gl.getParameter(gl.RENDERER);
    const vendor = gl.getParameter(gl.VENDOR);

    // Memory and performance indicators
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    const maxVertexAttribs = gl.getParameter(gl.MAX_VERTEX_ATTRIBS);

    // Device type detection
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(navigator.userAgent);
    const memoryGB = navigator.deviceMemory || 4; // Default to 4GB if unknown

    // Quality determination logic
    if (isMobile && !isTablet) {
      return this.getQualitySettings("low");
    } else if (isTablet || memoryGB < 4) {
      return this.getQualitySettings("medium");
    } else if (memoryGB >= 8 && maxTextureSize >= 4096) {
      return this.getQualitySettings("high");
    } else {
      return this.getQualitySettings("medium");
    }
  }

  detectLowPerformanceDevice() {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");

    if (!gl) return true;

    const renderer = gl.getParameter(gl.RENDERER).toLowerCase();
    const lowEndGPUs = ["mali", "adreno 3", "adreno 4", "powervr", "intel"];

    return lowEndGPUs.some((gpu) => renderer.includes(gpu)) || (navigator.deviceMemory && navigator.deviceMemory < 4);
  }

  getQualitySettings(quality) {
    const settings = {
      low: {
        starCount: 3000,
        nebulaLayers: 3,
        particleCount: 800,
        debrisCount: 500,
        renderScale: 0.75,
        antialias: false,
        shadowMap: false,
        maxLights: 2,
        textureQuality: 512,
        updateFrequency: 30, // Update every 30 frames instead of every frame
        blackHoleComplexity: "simple",
      },
      medium: {
        starCount: 5000,
        nebulaLayers: 4,
        particleCount: 1200,
        debrisCount: 800,
        renderScale: 0.9,
        antialias: true,
        shadowMap: false,
        maxLights: 4,
        textureQuality: 1024,
        updateFrequency: 15,
        blackHoleComplexity: "medium",
      },
      high: {
        starCount: 8000,
        nebulaLayers: 6,
        particleCount: 2000,
        debrisCount: 1500,
        renderScale: 1.0,
        antialias: true,
        shadowMap: true,
        maxLights: 8,
        textureQuality: 2048,
        updateFrequency: 1,
        blackHoleComplexity: "full",
      },
    };

    return settings[quality] || settings.medium;
  }

  initPerformanceMonitoring() {
    this.fpsHistory = [];
    this.frameTimeHistory = [];
    this.maxHistoryLength = 60; // Track last 60 frames

    // Create performance indicator UI
    this.createPerformanceIndicator();
  }

  createPerformanceIndicator() {
    const indicator = document.createElement("div");
    indicator.id = "performance-indicator";
    indicator.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      padding: 8px 12px;
      border-radius: 8px;
      font-family: "Exo 2", monospace;
      font-size: 11px;
      z-index: 200;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    `;

    document.body.appendChild(indicator);

    // Show/hide with Ctrl+P
    document.addEventListener("keydown", (e) => {
      if (e.ctrlKey && e.key === "p") {
        e.preventDefault();
        const currentOpacity = indicator.style.opacity;
        indicator.style.opacity = currentOpacity === "1" ? "0" : "1";
      }
    });

    this.performanceIndicator = indicator;
  }

  updatePerformanceStats(currentTime) {
    this.frameCount++;

    if (this.lastTime === 0) {
      this.lastTime = currentTime;
      return;
    }

    this.frameTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    // Calculate FPS
    if (this.frameTime > 0) {
      const currentFPS = 1000 / this.frameTime;
      this.fpsHistory.push(currentFPS);
      this.frameTimeHistory.push(this.frameTime);

      if (this.fpsHistory.length > this.maxHistoryLength) {
        this.fpsHistory.shift();
        this.frameTimeHistory.shift();
      }

      // Calculate average FPS
      this.fps = this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length;

      // Update performance indicator
      this.updatePerformanceIndicator();

      // Auto-adjust quality based on performance
      this.autoAdjustQuality();
    }
  }

  updatePerformanceIndicator() {
    if (!this.performanceIndicator) return;

    const avgFrameTime = this.frameTimeHistory.reduce((a, b) => a + b, 0) / this.frameTimeHistory.length;
    const quality = this.getQualityLabel();

    let color = "#00ff00"; // Green
    if (this.fps < 45) color = "#ffff00"; // Yellow
    if (this.fps < 30) color = "#ff0000"; // Red

    this.performanceIndicator.innerHTML = `
      <div style="color: ${color}">FPS: ${Math.round(this.fps)}</div>
      <div style="color: #ffffff; opacity: 0.8;">Frame: ${avgFrameTime.toFixed(1)}ms</div>
      <div style="color: #00ffff; opacity: 0.8;">Quality: ${quality}</div>
    `;
  }

  getQualityLabel() {
    if (this.qualitySettings.starCount <= 3000) return "Low";
    if (this.qualitySettings.starCount <= 5000) return "Medium";
    return "High";
  }

  autoAdjustQuality() {
    // Only adjust every 5 seconds to avoid flickering
    if (this.frameCount % 300 !== 0) return;

    const avgFPS = this.fps;
    const currentQuality = this.getQualityLabel().toLowerCase();

    // Downgrade quality if performance is poor
    if (avgFPS < 25 && currentQuality !== "low") {
      const newQuality = currentQuality === "high" ? "medium" : "low";
      this.adjustQuality(newQuality);
      this.showQualityNotification(`Quality reduced to ${newQuality} (Low FPS: ${Math.round(avgFPS)})`);
    }
    // Upgrade quality if performance is good and stable
    else if (avgFPS > 55 && currentQuality !== "high" && this.fpsHistory.length >= 60) {
      const stableFPS = this.fpsHistory.every((fps) => fps > 50);
      if (stableFPS) {
        const newQuality = currentQuality === "low" ? "medium" : "high";
        this.adjustQuality(newQuality);
        this.showQualityNotification(`Quality upgraded to ${newQuality} (Stable FPS: ${Math.round(avgFPS)})`);
      }
    }
  }

  adjustQuality(newQuality) {
    this.qualitySettings = this.getQualitySettings(newQuality);

    // Trigger quality change event
    const event = new CustomEvent("qualityChange", {
      detail: { quality: newQuality, settings: this.qualitySettings },
    });
    document.dispatchEvent(event);
  }

  showQualityNotification(message) {
    const notification = document.createElement("div");
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 80px;
      right: 10px;
      background: rgba(0, 0, 0, 0.8);
      color: #00ffff;
      padding: 8px 16px;
      border-radius: 6px;
      font-family: "Exo 2", sans-serif;
      font-size: 10px;
      z-index: 1001;
      opacity: 0;
      transition: opacity 0.3s ease;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 255, 255, 0.3);
      max-width: 200px;
    `;

    document.body.appendChild(notification);

    setTimeout(() => (notification.style.opacity = "1"), 100);
    setTimeout(() => (notification.style.opacity = "0"), 3000);
    setTimeout(() => notification.remove(), 3300);
  }

  // Methods for getting optimized settings
  getStarCount() {
    return this.qualitySettings.starCount;
  }
  getNebulaLayers() {
    return this.qualitySettings.nebulaLayers;
  }
  getParticleCount() {
    return this.qualitySettings.particleCount;
  }
  getDebrisCount() {
    return this.qualitySettings.debrisCount;
  }
  getRenderScale() {
    return this.qualitySettings.renderScale;
  }
  shouldUseAntialias() {
    return this.qualitySettings.antialias;
  }
  getUpdateFrequency() {
    return this.qualitySettings.updateFrequency;
  }
  getBlackHoleComplexity() {
    return this.qualitySettings.blackHoleComplexity;
  }

  // Manual quality override
  setQuality(quality) {
    this.qualitySettings = this.getQualitySettings(quality);
    this.adjustQuality(quality);
  }
}

// Create global performance manager instance
const performanceManager = new PerformanceManager();

export { performanceManager };
